import PropTypes from 'prop-types';
import { isValidElement, PureComponent } from 'react';
import classNames from 'classnames';
import devLogger from 'react-utils/devLogger';
import { toPx } from '../utils/Styles';
import UIClickable from '../button/UIClickable';
import UIIconCircle from '../icon/UIIconCircle';
import { ICON_SIZES } from '../icon/IconConstants';
import { LORAX, OLAF } from 'HubStyleTokens/colors';
import { BASE_LINE_HEIGHT } from 'HubStyleTokens/sizes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultStepIconComponent = ({
  defaultIcon
}) => defaultIcon;
const defaultProps = {
  circleSize: 10,
  direction: 'horizontal',
  responsiveBreakpoint: 500,
  StepIconComponent: DefaultStepIconComponent,
  use: 'default'
};
const getCircleSize = (size = defaultProps.circleSize) => typeof size === 'number' ? size : ICON_SIZES[size];
const getKeyFromStep = stepElement => {
  if (! /*#__PURE__*/isValidElement(stepElement)) return stepElement;
  const {
    key
  } = stepElement;
  if (process.env.NODE_ENV !== 'production' && typeof key !== 'string') {
    devLogger.warn({
      message: 'UIStepIndicator: stepName elements must have a `key`',
      key: 'UIStepIndicator-stepName-key'
    });
  }
  return key;
};
const renderStep = (step, circleSize, direction) => {
  const lineHeightOffset = parseInt(BASE_LINE_HEIGHT, 10) / 2;
  const labelSeparationX = 10;
  const itemOffsetX = direction === 'vertical' ? toPx(getCircleSize(circleSize) * 2 + labelSeparationX) : undefined; // undefined instead of null due to this error: Type 'null' is not assignable to type 'MarginTopProperty<string | number> | undefined'
  const itemOffsetY = direction === 'vertical' ? toPx(getCircleSize(circleSize) - lineHeightOffset) : undefined;
  return /*#__PURE__*/_jsx("div", {
    className: "private-step-indicator__item",
    style: {
      marginLeft: itemOffsetX,
      marginTop: itemOffsetY
    },
    children: /*#__PURE__*/_jsx("span", {
      className: "private-step-indicator__text",
      children: step
    })
  });
};
const renderSteps = (circleSize, direction, onStepClick, stepIndex, stepNames, StepIconComponent = defaultProps.StepIconComponent) => {
  let stepStatus = 'incomplete';
  return stepNames.map((stepName, index) => {
    if (index < stepIndex && stepIndex > 0) {
      stepStatus = 'complete';
    } else if (index === stepIndex) {
      stepStatus = 'active';
    } else if (stepStatus === 'active') {
      // If stepStatus is "active", then all subsequent steps are incomplete
      stepStatus = 'incomplete';
    }
    const clickableClass = onStepClick && index !== stepIndex ? 'private-step-indicator__clickable' : undefined;
    const classes = classNames(`private-step-indicator__section private-step-indicator__section--${stepStatus}`, clickableClass);
    const stepIcon = /*#__PURE__*/_jsx(UIIconCircle, {
      legacy: true,
      color: stepStatus === 'complete' ? OLAF : LORAX,
      backgroundColor: stepStatus === 'complete' ? LORAX : OLAF,
      borderColor: "currentColor",
      borderWidth: 2,
      className: "private-step-indicator__point",
      name: stepStatus === 'complete' ? 'success' : 'blank',
      size: getCircleSize(circleSize)
    });
    const StepIndicatorWrapper = onStepClick && index !== stepIndex ? UIClickable : 'div';
    const getStepAriaLabel = getKeyFromStep(stepName);
    const role = onStepClick && index !== stepIndex ? 'button' : 'listitem';
    const ariaLabel = stepStatus === 'complete' ? `${getStepAriaLabel} completed` : getStepAriaLabel;
    return /*#__PURE__*/_jsxs(StepIndicatorWrapper, {
      role: role,
      "aria-current": stepStatus === 'active' ? 'step' : false,
      "aria-label": ariaLabel,
      className: classes,
      onClick: onStepClick && index !== stepIndex ? () => onStepClick(index) : undefined,
      children: [/*#__PURE__*/_jsx("div", {
        className: "private-step-indicator__line",
        children: /*#__PURE__*/_jsx("span", {
          className: "private-step-indicator__point",
          children: /*#__PURE__*/_jsx(StepIconComponent, {
            stepIndex: stepIndex,
            stepStatus: stepStatus,
            index: index,
            circleSize: circleSize,
            defaultIcon: stepIcon
          })
        })
      }), renderStep(stepName, circleSize, direction)]
    }, getKeyFromStep(stepName));
  });
};
class UIStepIndicator extends PureComponent {
  constructor(props) {
    super(props);
    this.refCallback = ref => {
      this._el = ref;
      this.measureWidth();
    };
    this.measureWidth = () => {
      const {
        direction,
        responsiveBreakpoint = defaultProps.responsiveBreakpoint,
        use
      } = this.props;
      if (use === 'compact' || direction !== 'horizontal' || !this._el) return;
      if (this.state.isWidthConstrained) {
        this._el.classList.remove('private-step-indicator--compact');
      }

      // Measure width of wrapper and compare against threshold
      const isWidthConstrained = this._el.clientWidth < responsiveBreakpoint;
      if (isWidthConstrained !== this.state.isWidthConstrained) {
        this.setState({
          isWidthConstrained
        });
      }
      if (this.state.isWidthConstrained) {
        this._el.classList.add('private-step-indicator--compact');
      }
    };
    this.state = {
      isWidthConstrained: false
    };
    this._el = null;
    addEventListener('resize', this.measureWidth);
  }
  componentDidUpdate(prevProps, prevState) {
    // ⚠️ This condition prevents infinite loops! ⚠️
    if (prevState.isWidthConstrained === this.state.isWidthConstrained) {
      this.measureWidth();
    }
  }
  componentWillUnmount() {
    removeEventListener('resize', this.measureWidth);
  }
  render() {
    const {
      circleSize = defaultProps.circleSize,
      className,
      direction = defaultProps.direction,
      onStepClick,
      stepIndex,
      stepNames,
      StepIconComponent = defaultProps.StepIconComponent,
      use = defaultProps.use,
      'aria-label': ariaLabel
    } = this.props;
    const {
      isWidthConstrained
    } = this.state;
    const classes = classNames(className, 'private-step-indicator', (use === 'compact' || isWidthConstrained) && 'private-step-indicator--compact', StepIconComponent !== DefaultStepIconComponent && 'private-step-indicator--customized', {
      'horizontal': 'private-step-indicator--horizontal',
      'vertical': 'private-step-indicator--vertical'
    }[direction], use === 'flush' && 'private-step-indicator--flush private-step-indicator--compact');
    return /*#__PURE__*/_jsxs("div", {
      className: classes,
      ref: this.refCallback,
      "aria-label": ariaLabel,
      role: "list",
      children: [renderSteps(circleSize, direction, onStepClick, stepIndex, stepNames, StepIconComponent), direction === 'horizontal' ? /*#__PURE__*/_jsx("span", {
        "aria-hidden": true,
        className: "private-step-indicator__item private-step-indicator__status",
        children: stepNames[stepIndex]
      }) : null]
    });
  }
}
UIStepIndicator.propTypes = {
  circleSize: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(Object.keys(ICON_SIZES))]),
  direction: PropTypes.oneOf(['horizontal', 'vertical']).isRequired,
  responsiveBreakpoint: PropTypes.number,
  onStepClick: PropTypes.func,
  stepIndex: PropTypes.number,
  stepNames: PropTypes.arrayOf(PropTypes.node.isRequired).isRequired,
  StepIconComponent: PropTypes.elementType,
  use: PropTypes.oneOf(['default', 'compact', 'flush'])
};
UIStepIndicator.defaultProps = defaultProps;
UIStepIndicator.displayName = 'UIStepIndicator';
export default UIStepIndicator;